import type { RisqPrepTiming } from './content';
import { compareRisqCodes } from './codes';
import { isDone, type RisqStatus } from './status';

/**
 * "What to do now" from the owner's preparation flags (master file L–O, PLAN §2.10) and the
 * planned inspection date of the vessel (§2.11). Dates are calendar dates `YYYY-MM-DD`
 * (the phone's local date; the inspection date has no time).
 */

/** Preparation phase of a vessel: which flag's time has come. */
export const PREP_PHASES = [
  'early',
  'six_months',
  'three_months',
  'last',
  'final_24h',
  'passed',
] as const;
export type PrepPhase = (typeof PREP_PHASES)[number];

/** Flags in the order their time comes. */
export const PREP_TIMING_ORDER: readonly RisqPrepTiming[] = [
  'six_months',
  'three_months',
  'last',
  'final_24h',
];

/**
 * "Last" = the vessel's last voyage before the inspection (owner, Q26): it starts on the day the
 * office records as the start of the last voyage (D128). Until then, 7 days before the inspection.
 */
export const LAST_DAYS = 7;

/**
 * Questions without a timing flag (174 of 371 in RISQ 3.2) are treated as 3-m (owner, Q27).
 * The flag can be set per company in the content editor.
 */
export const DEFAULT_TIMING: RisqPrepTiming = 'three_months';

/** Timing flags of a question with the default applied: no flag = 3-m. */
export function effectiveTimings(timings: readonly RisqPrepTiming[]): readonly RisqPrepTiming[] {
  return timings.length === 0 ? [DEFAULT_TIMING] : timings;
}

/** The planned inspection of a vessel (dates `YYYY-MM-DD`). */
export interface InspectionPlan {
  plannedOn: string;
  /** Day the last voyage before the inspection started; null = not recorded yet. */
  lastVoyageStartedOn?: string | null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDate(value: string): Date {
  const match = ISO_DATE.exec(value);
  if (!match) {
    throw new RangeError(`Expected a date YYYY-MM-DD, got ${value}`);
  }
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (date.toISOString().slice(0, 10) !== value) {
    throw new RangeError(`Not a calendar date: ${value}`);
  }
  return date;
}

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function isIsoDate(value: string): boolean {
  try {
    parseDate(value);
    return true;
  } catch {
    return false;
  }
}

/** Calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseDate(to).getTime() - parseDate(from).getTime()) / 86_400_000);
}

export function addDays(date: string, days: number): string {
  const result = parseDate(date);
  result.setUTCDate(result.getUTCDate() + days);
  return formatDate(result);
}

/** Adds calendar months; the day is clamped to the month's length (31 Aug − 6 months = 28/29 Feb). */
export function addMonths(date: string, months: number): string {
  const start = parseDate(date);
  const target = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(start.getUTCDate(), lastDay));
  return formatDate(target);
}

/** The local calendar date of a moment, e.g. today on the phone. */
export function localDate(moment: Date): string {
  const y = moment.getFullYear();
  const m = String(moment.getMonth() + 1).padStart(2, '0');
  const d = String(moment.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** When a flag's work starts and the day by which it should be finished (both included). */
export interface PrepWindow {
  start: string;
  finishBy: string;
}

/**
 * The time of each flag (owner, 2026-11-05, D127): "6 months before" = start 12 months before
 * the inspection, finish by 6 months before; "3 months before" = start 6 months before, finish by
 * 3 months before; "Last voyage" = from the start of the last voyage (recorded by the office;
 * never later than the C/L day; else 7 days before) until the inspection; "Last 24 hours" = from
 * the day before until the inspection.
 */
export function prepWindows(plan: InspectionPlan): Record<RisqPrepTiming, PrepWindow> {
  const final24h = addDays(plan.plannedOn, -1);
  const voyage = plan.lastVoyageStartedOn ?? null;
  return {
    six_months: {
      start: addMonths(plan.plannedOn, -12),
      finishBy: addMonths(plan.plannedOn, -6),
    },
    three_months: {
      start: addMonths(plan.plannedOn, -6),
      finishBy: addMonths(plan.plannedOn, -3),
    },
    last: {
      start:
        voyage === null
          ? addDays(plan.plannedOn, -LAST_DAYS)
          : daysBetween(voyage, final24h) < 0
            ? final24h
            : voyage,
      finishBy: plan.plannedOn,
    },
    final_24h: { start: final24h, finishBy: plan.plannedOn },
  };
}

/** The day each flag's work starts (`prepWindows`). */
export function prepStartDates(plan: InspectionPlan): Record<RisqPrepTiming, string> {
  const windows = prepWindows(plan);
  return {
    six_months: windows.six_months.start,
    three_months: windows.three_months.start,
    last: windows.last.start,
    final_24h: windows.final_24h.start,
  };
}

/** The phase = the latest flag whose time has started; `passed` after the inspection day. */
export function prepPhase(today: string, plan: InspectionPlan): PrepPhase {
  if (daysBetween(today, plan.plannedOn) < 0) {
    return 'passed';
  }
  const starts = prepStartDates(plan);
  for (const timing of [...PREP_TIMING_ORDER].reverse()) {
    if (daysBetween(starts[timing], today) >= 0) {
      return timing;
    }
  }
  return 'early';
}

/**
 * When a question's work is due: `late` — the finish-by day of one of its flags has passed;
 * `now` — today is inside the time of one of its flags; `later` — all its flags are still ahead.
 * No timing flag counts as 3-m.
 */
export type PrepDue = 'late' | 'now' | 'later';

function dueOfTiming(window: PrepWindow, today: string): PrepDue {
  if (daysBetween(window.finishBy, today) > 0) {
    return 'late';
  }
  return daysBetween(window.start, today) >= 0 ? 'now' : 'later';
}

export function prepDue(
  flags: readonly RisqPrepTiming[],
  today: string,
  plan: InspectionPlan,
): PrepDue {
  const windows = prepWindows(plan);
  const dues = effectiveTimings(flags).map((t) => dueOfTiming(windows[t], today));
  return dues.includes('late') ? 'late' : dues.includes('now') ? 'now' : 'later';
}

export interface PrepItem {
  code: string;
  status: RisqStatus;
  prepTiming: readonly RisqPrepTiming[];
}

const DUE_ORDER: Record<PrepDue, number> = { late: 0, now: 1, later: 2 };

function earliestTiming(timings: readonly RisqPrepTiming[]): number {
  return Math.min(...effectiveTimings(timings).map((t) => PREP_TIMING_ORDER.indexOf(t)));
}

/**
 * Open questions whose time has come: late ones first, then those of now; inside each, the
 * earliest flag first, then by code. Done questions (Closed, Office action) are left out.
 */
export function whatToDoNow<T extends PrepItem>(
  items: readonly T[],
  today: string,
  plan: InspectionPlan,
): T[] {
  const due = new Map(items.map((item) => [item, prepDue(item.prepTiming, today, plan)]));
  return items
    .filter((item) => !isDone(item.status) && due.get(item) !== 'later')
    .sort(
      (a, b) =>
        DUE_ORDER[due.get(a) ?? 'later'] - DUE_ORDER[due.get(b) ?? 'later'] ||
        earliestTiming(a.prepTiming) - earliestTiming(b.prepTiming) ||
        compareRisqCodes(a.code, b.code),
    );
}

export interface PrepSummary {
  phase: PrepPhase;
  daysLeft: number;
  starts: Record<RisqPrepTiming, string>;
  windows: Record<RisqPrepTiming, PrepWindow>;
  /** "Last" starts with the recorded last voyage (true) or 7 days before (false). */
  lastFromVoyage: boolean;
  /** Start of the next phase, or null when the last one has begun. */
  next: { timing: RisqPrepTiming; date: string } | null;
}

export function prepSummary(today: string, plan: InspectionPlan): PrepSummary {
  const phase = prepPhase(today, plan);
  const windows = prepWindows(plan);
  const starts = prepStartDates(plan);
  const upcoming = PREP_TIMING_ORDER.find((t) => daysBetween(today, starts[t]) > 0);
  return {
    phase,
    daysLeft: daysBetween(today, plan.plannedOn),
    starts,
    windows,
    lastFromVoyage: (plan.lastVoyageStartedOn ?? null) !== null,
    next: upcoming === undefined ? null : { timing: upcoming, date: starts[upcoming] },
  };
}

/** Earliest and latest day the last voyage may have started (same rule as the server). */
export function lastVoyageRange(today: string, plannedOn: string): { from: string; to: string } {
  const tomorrow = addDays(today, 1);
  return {
    from: addMonths(plannedOn, -6),
    to: daysBetween(tomorrow, plannedOn) < 0 ? plannedOn : tomorrow,
  };
}
